"use client"

import { useLocale, useTranslations } from "next-intl"
import { goKeys as k } from "@/i18n/go-keys"
import { useCountry } from "@/components/layout/country-provider"
import {
  bookingUrlForCountry,
  PRODUCT_IDS,
  BOOKING_PACKAGE_TYPE,
} from "@/lib/booking"

export const SECTION = "mx-auto max-w-[1440px] px-6 md:px-10"

/* ── Booking destination for the "full check-up" CTA (blood + full-body MRI):
   the Advanced package, opened on the MRI package tab. The Advanced tier is
   the one carrying the dedicated contrast-free pelvic sequence this LP's MRI
   beat sells — never bare Core. ── */
export function useFullCheckupHref(): string {
  const locale = useLocale()
  const { code: country } = useCountry()
  return bookingUrlForCountry(
    country,
    locale,
    PRODUCT_IDS.advanced,
    undefined,
    BOOKING_PACKAGE_TYPE.mri
  )
}

/* ── CTA label with a price suffix that drops on mobile. Splits on the " · "
   separator and hides the trailing price part below `sm` so narrow CTAs keep
   the verb but lose the price tail. Labels with no " · " render whole. ── */
export function CtaLabel({ label }: { label: string }) {
  const m = label.match(/\s[·•∙‧]\s/)
  if (!m || m.index === undefined) return <>{label}</>
  return (
    <>
      {label.slice(0, m.index)}
      <span className="hidden sm:inline">{label.slice(m.index)}</span>
    </>
  )
}

/* ── Risk-reversal reassurance line shown under key primary CTAs (not the
   hero). 14px; `tone` adapts to light vs dark sections. ── */
export function CtaReassurance({
  tone = "onLight",
  className = "",
}: {
  tone?: "onLight" | "onDark"
  className?: string
}) {
  const t = useTranslations("go")
  return (
    <p
      className={`text-sm ${tone === "onDark" ? "text-white/70" : "text-neutral-500"} ${className}`}
    >
      {t(k.ctaReassurance)}
    </p>
  )
}
